import React from 'react';
import {
	ActivityIndicator,
	NativeScrollEvent,
	NativeSyntheticEvent,
	StyleSheet,
	Text,
	View,
	ViewStyle,
} from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { BgLandscapePng, BgPortraitPng } from '../../../assets';
import { DuelIconClockSvg, DuelIconPersonSvg } from '../../../assets/duels';
import { BackArrowSvg } from '../../../assets/event';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { useHeaderTopOffset } from '../../Pages/headerOffset';
import { MainLayout } from '../../Pages/MainLayout';
import { getNavRowHeight } from '../AppFooter/Nav/FooterNavButtonsV2';
import { GoldGradientText } from '../common/GoldGradientText';
import { GradientFill } from '../common/GradientFill';
import { IndicatorScrollView, IndicatorScrollViewProps } from '../common/ScrollIndicator';
import { TouchableOpacity } from '../common/Touchable';
import { desktopScrollFade, useDesktopLayout } from '../Desktop/desktopLayout';
import EventPanel from '../Event/EventPanel';
import EventScrollFade from '../Event/EventScrollFade';
import {
	DESIGN_CONTENT_WIDTH,
	DESIGN_FRAME_WIDTH,
	DESIGN_SECTION_GAP,
	EVENT_FONT,
	eventColors,
	GOLD_RAMP,
	MAX_SCALE,
	PROGRESS_TRACK_FILL,
} from '../Event/eventTheme';
import { HEADER_BACK_ROW_HEIGHT } from '../Top/TopSection';
import {
	DUEL_CLAIM_BORDER,
	DUEL_CLAIM_FILL,
	DUEL_PANEL_FILL,
	DUEL_SECONDARY_FILL,
	duelColors,
} from './duelsTheme';

const FADE_LEAD_IN = 44;
const TITLE_ROW_HEIGHT = 40;
const DESKTOP_COLUMN_MAX = 520;
const END_REACHED_THRESHOLD = 240;

export type DuelsPageLayout = { width: number; scale: number };

type DuelsPageProps = {
	title: string;
	onBack: () => void;
	children: (layout: DuelsPageLayout) => React.ReactNode;
	onEndReached?: () => void;
	avoidKeyboard?: IndicatorScrollViewProps['avoidKeyboard'];
	resetScrollKey?: string;
};

const DuelsPageBody = ({
	title,
	onBack,
	children,
	onEndReached,
	avoidKeyboard,
	resetScrollKey,
}: DuelsPageProps) => {
	const { width, height, insets } = useSafeViewport();
	const { isDesktop } = useDevice();
	const desktop = useDesktopLayout();
	const headerOffset = useHeaderTopOffset();
	const backdrop = isDesktop ? BgLandscapePng : BgPortraitPng;

	const contentWidth = desktop.isDesktopLayout
		? Math.min(desktop.contentWidth, DESKTOP_COLUMN_MAX)
		: DESIGN_CONTENT_WIDTH * Math.min(width / DESIGN_FRAME_WIDTH, MAX_SCALE);
	const scale = contentWidth / DESIGN_CONTENT_WIDTH;
	const gap = DESIGN_SECTION_GAP * scale;

	const headerSolid = desktop.isDesktopLayout ? 0 : headerOffset + HEADER_BACK_ROW_HEIGHT;
	const pinnedTitle = !desktop.isDesktopLayout;
	const topSolid = pinnedTitle ? headerSolid + TITLE_ROW_HEIGHT * scale + gap / 2 : headerSolid;
	const bottomClearance = desktop.isDesktopLayout
		? desktop.s(40)
		: getNavRowHeight(width, height, true) + insets.bottom;

	const handleScroll = onEndReached
		? (event: NativeSyntheticEvent<NativeScrollEvent>) => {
				const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
				const remaining = contentSize.height - contentOffset.y - layoutMeasurement.height;
				if (remaining < END_REACHED_THRESHOLD) onEndReached();
			}
		: undefined;

	const titleRow = (
		<View style={[styles.titleRow, { width: contentWidth, height: TITLE_ROW_HEIGHT * scale }]}>
			<TouchableOpacity
				onPress={onBack}
				style={[styles.back, { width: 32 * scale, height: TITLE_ROW_HEIGHT * scale }]}
				accessibilityRole='button'
			>
				<BackArrowSvg width={12 * scale} height={20 * scale} />
			</TouchableOpacity>
			<GoldGradientText
				text={title}
				width={contentWidth - 64 * scale}
				height={TITLE_ROW_HEIGHT * scale}
				fontSize={24 * scale}
				fontWeight='800'
			/>
			<View style={{ width: 32 * scale }} />
		</View>
	);

	return (
		<View style={styles.page}>
			<IndicatorScrollView
				key={resetScrollKey}
				style={[styles.scroll, desktop.isDesktopLayout && desktopScrollFade(desktop.s(40))]}
				contentContainerStyle={[
					styles.content,
					{
						rowGap: gap,
						paddingTop: topSolid + (desktop.isDesktopLayout ? desktop.s(8) : gap / 2),
						paddingBottom: bottomClearance + gap,
					},
				]}
				showsVerticalScrollIndicator={false}
				indicatorInsets={{ top: topSolid, bottom: bottomClearance, right: 4 }}
				reserveGutter={false}
				onScroll={handleScroll}
				scrollEventThrottle={100}
				avoidKeyboard={avoidKeyboard}
			>
				{pinnedTitle ? null : titleRow}
				{children({ width: contentWidth, scale })}
			</IndicatorScrollView>

			{desktop.isDesktopLayout ? null : (
				<>
					<EventScrollFade
						edge='top'
						source={backdrop}
						viewportWidth={width}
						viewportHeight={height}
						height={topSolid + FADE_LEAD_IN}
						solid={topSolid}
					/>
					<EventScrollFade
						edge='bottom'
						source={backdrop}
						viewportWidth={width}
						viewportHeight={height}
						height={bottomClearance + FADE_LEAD_IN}
						solid={bottomClearance}
					/>
					<View style={[styles.pinnedTitle, { top: headerSolid }]}>{titleRow}</View>
				</>
			)}
		</View>
	);
};

export const DuelsPage = (props: DuelsPageProps) => (
	<MainLayout fullBleed>
		<DuelsPageBody {...props} />
	</MainLayout>
);

type DuelPanelProps = {
	scale: number;
	width: number;
	children: React.ReactNode;
	style?: ViewStyle;
	highlighted?: boolean;
};

export const DuelPanel = ({ scale, width, children, style, highlighted }: DuelPanelProps) => (
	<EventPanel
		radius={12 * scale}
		borderWidth={Math.max(1, 1 * scale)}
		borderColor={highlighted ? eventColors.panelBorderBright : eventColors.countdownBorder}
		fill={DUEL_PANEL_FILL}
		style={[{ width, padding: 12 * scale, rowGap: 8 * scale }, style ?? {}]}
	>
		{children}
	</EventPanel>
);

type DuelButtonVariant = 'gold' | 'dark' | 'green';

const DUEL_BUTTON_LOOK: Record<
	DuelButtonVariant,
	{ fill: Array<[string, string]>; border: string; label: string }
> = {
	gold: { fill: GOLD_RAMP, border: eventColors.goldPale, label: '#3A1200' },
	dark: { fill: DUEL_SECONDARY_FILL, border: eventColors.panelBorder, label: eventColors.goldSoft },
	green: { fill: DUEL_CLAIM_FILL, border: DUEL_CLAIM_BORDER, label: '#FFFFFF' },
};

type DuelButtonProps = {
	label: string;
	onPress: () => void;
	width: number;
	scale: number;
	variant?: DuelButtonVariant;
	disabled?: boolean;
	busy?: boolean;
	icon?: React.ReactNode;
	labelSize?: number;
	height?: number;
};

export const DuelButton = ({
	label,
	onPress,
	width,
	scale,
	variant = 'gold',
	disabled,
	busy,
	icon,
	labelSize = 17,
	height: designHeight = 44,
}: DuelButtonProps) => {
	const look = DUEL_BUTTON_LOOK[variant];
	const height = designHeight * scale;
	const radius = 10 * scale;

	return (
		<TouchableOpacity
			onPress={onPress}
			disabled={disabled || busy}
			accessibilityRole='button'
			accessibilityState={{ disabled: !!(disabled || busy), busy: !!busy }}
			style={[
				styles.button,
				{
					width,
					height,
					borderRadius: radius,
					borderWidth: Math.max(1, 1 * scale),
					borderColor: look.border,
					opacity: disabled ? 0.5 : 1,
				},
			]}
		>
			<View pointerEvents='none' style={[StyleSheet.absoluteFill, { borderRadius: radius }]}>
				<GradientFill stops={look.fill} radius={radius} />
			</View>
			{busy ? (
				<ActivityIndicator color={look.label} />
			) : (
				<View
					style={[styles.buttonContent, { columnGap: 8 * scale, paddingHorizontal: 6 * scale }]}
				>
					{icon}
					<Text
						numberOfLines={1}
						style={[
							styles.buttonLabel,
							{
								fontSize: labelSize * scale,
								color: look.label,
							},
						]}
					>
						{label}
					</Text>
				</View>
			)}
		</TouchableOpacity>
	);
};

type ProgressBarProps = { value: number; width: number; scale: number };

export const DuelProgressBar = ({ value, width, scale }: ProgressBarProps) => {
	const trackId = useSvgId('duelProgressTrack');
	const fillId = useSvgId('duelProgressFill');
	const filled = Math.max(0, Math.min(1, value));
	const height = 10 * scale;

	return (
		<View style={{ width, height, borderRadius: height / 2, overflow: 'hidden' }}>
			<Svg width='100%' height='100%'>
				<Defs>
					<LinearGradient id={trackId} x1='0' y1='0' x2='0' y2='1'>
						{PROGRESS_TRACK_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
					<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
						{GOLD_RAMP.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
				</Defs>
				<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${trackId})`} />
				<Rect x='0' y='0' width={`${filled * 100}%`} height='100%' fill={`url(#${fillId})`} />
			</Svg>
		</View>
	);
};

type NoticeProps = {
	text: string;
	scale: number;
	width: number;
	actionLabel?: string;
	onAction?: () => void;
};

export const DuelNotice = ({ text, scale, width, actionLabel, onAction }: NoticeProps) => (
	<DuelPanel scale={scale} width={width}>
		<Text style={[duelTextStyles.body, { fontSize: 14 * scale, textAlign: 'center' }]}>{text}</Text>
		{actionLabel && onAction ? (
			<DuelButton
				label={actionLabel}
				onPress={onAction}
				width={width - 24 * scale}
				scale={scale}
				variant='dark'
			/>
		) : null}
	</DuelPanel>
);

type PillProps = { label: string; scale: number };

export const DuelPill = ({ label, scale }: PillProps) => (
	<View
		style={[
			styles.pill,
			{
				columnGap: 4 * scale,
				paddingHorizontal: 8 * scale,
				paddingVertical: 3 * scale,
				borderRadius: 999,
				borderWidth: Math.max(1, 1 * scale),
			},
		]}
	>
		<Text style={[duelTextStyles.cream, { fontSize: 10 * scale }]}>{label}</Text>
	</View>
);

type FiguresProps = { finished: number; participants: number; slots: number; scale: number };

export const finishedFigureSlots = (finished: number, participants: number, slots: number) => {
	const total = Math.max(0, participants);
	if (total <= slots) return { total, filled: Math.min(Math.max(0, finished), total) };
	if (finished <= 0) return { total: slots, filled: 0 };
	if (finished >= total) return { total: slots, filled: slots };
	const filled = Math.round((finished / total) * slots);
	return { total: slots, filled: Math.min(slots - 1, Math.max(1, filled)) };
};

export const DuelFinishedFigures = ({ finished, participants, slots, scale }: FiguresProps) => {
	const { total, filled } = finishedFigureSlots(finished, participants, slots);
	if (!total) return null;
	return (
		<View style={[styles.figures, { columnGap: 4 * scale }]}>
			{Array.from({ length: total }, (_, index) => (
				<View key={index} style={index < filled ? null : styles.dimmed}>
					<DuelIconPersonSvg width={18 * scale} height={18 * scale} />
				</View>
			))}
		</View>
	);
};

type CountdownProps = { text: string; scale: number; fontSize?: number; white?: boolean };

export const DuelClockText = ({ text, scale, fontSize = 13, white }: CountdownProps) => (
	<View style={[styles.clockRow, { columnGap: 6 * scale }]}>
		<DuelIconClockSvg width={(fontSize + 3) * scale} height={(fontSize + 3) * scale} />
		<Text
			style={[white ? duelTextStyles.title : duelTextStyles.cream, { fontSize: fontSize * scale }]}
		>
			{text}
		</Text>
	</View>
);

export const DuelLoading = () => (
	<View style={styles.loading}>
		<ActivityIndicator color={eventColors.gold} />
	</View>
);

export const duelTextStyles = StyleSheet.create({
	title: {
		fontFamily: EVENT_FONT,
		fontWeight: '800',
		color: eventColors.white,
	},
	gold: {
		fontFamily: EVENT_FONT,
		fontWeight: '800',
		color: eventColors.gold,
	},
	body: {
		fontFamily: EVENT_FONT,
		fontWeight: '600',
		color: eventColors.label,
	},
	muted: {
		fontFamily: EVENT_FONT,
		fontWeight: '600',
		color: duelColors.muted,
	},
	cream: {
		fontFamily: EVENT_FONT,
		fontWeight: '700',
		color: eventColors.goldSoft,
	},
});

const styles = StyleSheet.create({
	page: {
		flex: 1,
		minHeight: 0,
		alignItems: 'center',
		width: '100%',
	},
	scroll: {
		flex: 1,
		minHeight: 0,
		width: '100%',
	},
	content: {
		alignItems: 'center',
	},
	pinnedTitle: {
		position: 'absolute',
		left: 0,
		right: 0,
		alignItems: 'center',
	},
	titleRow: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	back: {
		alignItems: 'flex-start',
		justifyContent: 'center',
	},
	button: {
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	buttonContent: {
		flexDirection: 'row',
		alignItems: 'center',
		maxWidth: '100%',
	},
	buttonLabel: {
		flexShrink: 1,
		fontFamily: EVENT_FONT,
		fontWeight: '900',
		letterSpacing: 0.5,
	},
	loading: {
		paddingVertical: 32,
		alignItems: 'center',
	},
	pill: {
		flexDirection: 'row',
		alignItems: 'center',
		alignSelf: 'flex-start',
		borderColor: eventColors.goldSoft,
	},
	figures: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	dimmed: {
		opacity: 0.35,
	},
	clockRow: {
		flexDirection: 'row',
		alignItems: 'center',
	},
});
